import { z } from 'zod'

export const CLASS_PLACEMENT_KEYS = [
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  '9th',
  '10th',
] as const

export type ClassPlacementKey = (typeof CLASS_PLACEMENT_KEYS)[number]

/** Places a show awards in each class unless the class sets its own. */
export const SHOW_PLACE_COUNTS = [6, 3] as const
export const DEFAULT_PLACE_COUNT = 6
export const ShowPlaceCount = z.union([z.literal(6), z.literal(3)])

/** A class's own place count: one place up to every place results can record. */
export const ClassPlaceCount = z.number().int().min(1).max(CLASS_PLACEMENT_KEYS.length)

/** "1 place" or "6 places". */
export function formatPlaceCount(count: number): string {
  return count === 1 ? '1 place' : `${count} places`
}

/** "1 place (1st)" or "6 places (1st–6th)". */
export function formatPlaceRange(count: number): string {
  const last = CLASS_PLACEMENT_KEYS[count - 1]
  return `${formatPlaceCount(count)} (${count === 1 ? last : `1st–${last}`})`
}

/** The places a class awards: its own count, else its show's default. */
export function classPlaceCount(
  cls: { placeCount?: number | null },
  show: { defaultPlaceCount?: number | null }
): number {
  return cls.placeCount ?? show.defaultPlaceCount ?? DEFAULT_PLACE_COUNT
}

/**
 * The places a class can record: its configured places, extended to the lowest place that
 * already holds a saved result. Results saved before the count was lowered, or before classes
 * had a count at all, stay visible and editable.
 */
export function recordablePlaces(
  placeCount: number,
  savedPlaces: Iterable<string> = []
): ClassPlacementKey[] {
  let count = placeCount
  for (const place of savedPlaces) {
    count = Math.max(count, CLASS_PLACEMENT_KEYS.indexOf(place as ClassPlacementKey) + 1)
  }
  return CLASS_PLACEMENT_KEYS.slice(0, count)
}

/**
 * Leaderboard placement columns: every place the show awards, then any other place an entrant
 * holds, such as in a class with its own count or with results saved before classes had one.
 */
export function leaderboardPlaces(
  placementCounts: Iterable<Record<string, number>>,
  defaultPlaceCount: number
): string[] {
  const held = new Set<string>()
  for (const counts of placementCounts)
    for (const [place, count] of Object.entries(counts)) if (count > 0) held.add(place)
  const standard: readonly string[] = CLASS_PLACEMENT_KEYS
  return [
    ...standard.filter((place, index) => index < defaultPlaceCount || held.has(place)),
    ...[...held]
      .filter(place => !standard.includes(place))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
  ]
}
